import { useQuery } from '@tanstack/react-query';

import { rawFileContentQuery } from '@/features/pulls/services/files';

export interface RawFileContentResult {
  content: string | null;
  loading: boolean;
  error: string | null;
  fetchContent: () => Promise<string>;
}

/**
 * Hook to manage fetching and caching raw file content from GitHub
 * @param owner - Repository owner
 * @param repo - Repository name
 * @param sha - Commit SHA
 * @param filePath - Path to the file
 * @returns Object with content, loading state, error state, and fetch function
 */
export function useRawFileContent(
  owner?: string,
  repo?: string,
  sha?: string,
  filePath?: string
): RawFileContentResult {
  const hasAllParams = !!(owner && repo && sha && filePath);

  const query = useQuery({
    ...rawFileContentQuery(owner!, repo!, sha!, filePath!),
    enabled: false, // lazy load files
  });

  return {
    content: query.data ?? null,
    loading: query.isLoading,
    error: query.error?.message ?? null,
    fetchContent: async () => {
      if (!hasAllParams) {
        throw new Error('Missing required parameters (owner, repo, sha, or filePath)');
      }

      if (query.data !== undefined) {
        return query.data;
      }

      const result = await query.refetch();

      if (result.error) {
        throw result.error;
      }
      return result.data ?? '';
    },
  };
}
